// ----------------------------------------------------------------------------
// Product Form Extension: Retain Credit Amount Display
// ----------------------------------------------------------------------------
// Dynamically updates the retain credit amount and purchase type display
// when the variant or selling plan changes
// ----------------------------------------------------------------------------
// Event Listeners:
// - 'productForm:variant:changed' → Updates credit amount when variant changes
// - 'productForm:sellingPlanInput:changed' → Updates credit amount and purchase type when selling plan changes
// ----------------------------------------------------------------------------
// Key Data Mutations:
// - Updates the retain credit amount based on the current variant price and selling plan
// - Updates the purchase type display (one-time vs Subscribe & Save)
// ----------------------------------------------------------------------------
// Gotchas:
// - The retain credit amount is calculated based on the effective price (selling plan price if subscription, variant price if one-time)
// - The percentage is stored in data-retain-credit-percentage attribute
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import { formatMoney } from '@shopify/theme-currency';

// -------------------------------------
// Helper Functions
// -------------------------------------
/**
 * Get the effective price for the credit calculation
 * @param {Object} variant - The current variant
 * @param {Object|null} sellingPlan - The current selling plan (if any)
 * @returns {number} - The price in cents
 */
function getEffectivePrice(variant, sellingPlan) {
  if (!variant) return 0;

  // If subscription is selected, calculate the discounted price
  if (sellingPlan && sellingPlan.purchase_type === 'subscription') {
    // Get the discount percentage from the product price element
    var priceContainer = document.querySelector('iam-product-form .product__price');
    var discountPercentage = priceContainer ? parseFloat(priceContainer.dataset.discountPercentage || 0) : 0;
    if (discountPercentage > 0) {
      // Calculate discounted price using the same logic as product-price.js
      return Math.round(variant.price * (1 - discountPercentage / 100));
    }

    // Fallback to selling plan price if no discount percentage is available
    return sellingPlan.price || variant.price;
  }
  return variant.price;
}

/**
 * Get the purchase type text
 * @param {Object|null} sellingPlan - The current selling plan (if any)
 * @returns {string} - The purchase type text
 */
function getPurchaseType(sellingPlan) {
  var _window3;
  // If no selling plan, or it's an onetime plan, return one-time purchase
  if (!sellingPlan || sellingPlan.purchase_type === 'onetime' || sellingPlan.group_id === 'onetime') {
    var _window;
    return ((_window = window) === null || _window === void 0 || (_window = _window.translations) === null || _window === void 0 || (_window = _window.sellingPlan) === null || _window === void 0 ? void 0 : _window.onetime) || 'One-time Purchase';
  }

  // If it's a subscription plan, return Subscribe & Save
  if (sellingPlan.purchase_type === 'subscription' || sellingPlan.purchase_type === 'selling_plan') {
    var _window2;
    return ((_window2 = window) === null || _window2 === void 0 || (_window2 = _window2.translations) === null || _window2 === void 0 || (_window2 = _window2.sellingPlan) === null || _window2 === void 0 ? void 0 : _window2.subscribe_and_save) || 'Subscribe & Save';
  }

  // Default to one-time purchase
  return ((_window3 = window) === null || _window3 === void 0 || (_window3 = _window3.translations) === null || _window3 === void 0 || (_window3 = _window3.sellingPlan) === null || _window3 === void 0 ? void 0 : _window3.onetime) || 'One-time Purchase';
}

/**
 * Update the retain credit display
 * @param {Object} params - Parameters containing variant, selling plan, and form
 */
function updateRetainCredit(_ref) {
  var _window4;
  var {
    variant,
    sellingPlan
  } = _ref;
  var creditElement = document.querySelector('.product-info__item--retain-credit');
  if (!creditElement) return;
  var percentage = parseFloat(creditElement.dataset.retainCreditPercentage || 0);
  if (!percentage || percentage <= 0) return;
  var effectivePrice = getEffectivePrice(variant, sellingPlan);
  if (!effectivePrice) return;

  // Calculate credit amount (percentage is stored as a number, e.g. 5 for 5%)
  var creditAmount = effectivePrice * (percentage / 100);
  var formattedCredit = formatMoney(creditAmount);
  var purchaseType = getPurchaseType(sellingPlan);

  // Get the translation template
  var translationTemplate = ((_window4 = window) === null || _window4 === void 0 || (_window4 = _window4.translations) === null || _window4 === void 0 ? void 0 : _window4.retainCreditAmountHtml) || 'You earn <b>{{ retain_credit_amount }}</b> in credit with {{ purchase_type }}';

  // Replace placeholders in the translation template
  var creditText = translationTemplate.replace('{{ retain_credit_amount }}', formattedCredit).replace('{{ purchase_type }}', purchaseType);
  var textElement = creditElement.querySelector('[data-retain-credit-text]');
  if (textElement) {
    textElement.innerHTML = creditText;
  }
}

/**
 * Get the current selling plan from the form
 * @param {Object} iamProductForm - The product form instance
 * @returns {Object|null} - The current selling plan
 */
function getCurrentSellingPlan(iamProductForm) {
  if (!iamProductForm) return null;
  var sellingPlanInput = iamProductForm.querySelector('iam-selling-plan-input');
  if (!sellingPlanInput) return null;
  return sellingPlanInput.getCurrentSellingPlan();
}

// -------------------------------------
// Event Listeners
// -------------------------------------

// Update when variant changes
iamotaEventHandler.subscribe('productForm:variant:changed', _ref2 => {
  var {
    iamProductForm,
    variant
  } = _ref2;
  if (!iamProductForm || !variant) {
    return;
  }
  var sellingPlan = getCurrentSellingPlan(iamProductForm);
  updateRetainCredit({
    variant,
    sellingPlan,
    iamProductForm
  });
}, {
  source: "product-form--retain-credit.js"
});

// Update when selling plan changes
iamotaEventHandler.subscribe('productForm:sellingPlanInput:changed', _ref3 => {
  var {
    sellingPlan,
    _options
  } = _ref3;
  // Get the selling plan input component from the context
  var sellingPlanInput = _options === null || _options === void 0 ? void 0 : _options.context;
  if (!sellingPlanInput) {
    return;
  }

  // Get the product form from the selling plan input
  var iamProductForm = sellingPlanInput.iamForm;
  if (!iamProductForm) {
    return;
  }
  var variant = iamProductForm === null || iamProductForm === void 0 ? void 0 : iamProductForm.getCurrentVariant();
  if (!variant) {
    return;
  }
  updateRetainCredit({
    variant,
    sellingPlan,
    iamProductForm
  });
}, {
  source: "product-form--retain-credit.js"
});

// Update when selling plan changes (new event name)
iamotaEventHandler.subscribe('sellingPlanInput:changed', _ref4 => {
  var {
    sellingPlan,
    _options
  } = _ref4;
  // Get the selling plan input component from the context
  var sellingPlanInput = _options === null || _options === void 0 ? void 0 : _options.context;
  if (!sellingPlanInput) {
    return;
  }

  // Get the product form from the selling plan input
  var iamProductForm = sellingPlanInput.iamForm;
  if (!iamProductForm) {
    return;
  }
  var variant = iamProductForm === null || iamProductForm === void 0 ? void 0 : iamProductForm.getCurrentVariant();
  if (!variant) {
    return;
  }
  updateRetainCredit({
    variant,
    sellingPlan,
    iamProductForm
  });
}, {
  source: "product-form--retain-credit.js"
});

// Update on form init to ensure initial state is correct
iamotaEventHandler.subscribe('productForm:init', _ref5 => {
  var {
    iamForm
  } = _ref5;
  if (!iamForm) return;
  var variant = iamForm === null || iamForm === void 0 ? void 0 : iamForm.getCurrentVariant();
  if (!variant) return;
  var sellingPlan = getCurrentSellingPlan(iamForm);
  updateRetainCredit({
    variant,
    sellingPlan,
    iamProductForm: iamForm
  });
}, {
  source: "product-form--retain-credit.js"
});